import {
  type AuditEntry,
  type ErrorMessage,
  type InspectReplyMessage,
  type SightedCamera,
  type Interference,
  type Obstruction,
  type CommunityMarker,
  type DispatchBoardMessage,
  type JobBoardMessage,
  type CountySources,
  type SideChangedMessage,
  type WeekMessageReply,
  type StandingReplyMessage,
  PLATE_MEMORY_SECONDS,
  PROTOCOL_VERSION,
  SNAPSHOT_HZ,
  TICK_SECONDS,
  createWorld,
  encode,
  spawnVehicle,
  stepVehicle,
  tryDecode,
  type County,
  type Plate,
  type AdminStateMessage,
  type ScannerMessage,
  type AlertMessage,
  type DetectionRow,
  type HotlistRow,
  type StopResultMessage,
  type PursuitMessage,
  type PlateRead,
  type QueryResultMessage,
  type HeatCell,
  type IncidentRow,
  type JourneyPoint,
  type AdminEventMessage,
  type RideAlongStateMessage,
  type CameraDetailReplyMessage,
  type ServerMessage,
  type SnapshotMessage,
  type Vehicle,
  type World,
  type Router,
} from '@hotlist/shared';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { Keyring, type CarKey } from './keyring.ts';
import type { ConnectionState, Renderable } from './state.ts';

/**
 * The connection to the authoritative server.
 *
 * DESIGN.md section 5: interpolate others, predict self.
 *
 * **Others are interpolated.** The server sends a position and a heading and
 * nothing else, fifteen times a second. This keeps the last two of those per
 * vehicle and draws between them, which is why traffic moves smoothly on a
 * display that refreshes four times faster than the simulation ticks.
 *
 * **Self is predicted**, by running the same tick function the server runs,
 * over the same road graph, using the route the server sent. This client is
 * entitled to its own route and to no other vehicle's, so that is the only
 * thing it can predict, which is exactly the intended shape.
 *
 * Reconciliation is deliberately blunt. When the server's position for this
 * vehicle disagrees with the predicted one by more than a car length, the
 * prediction is wrong about something real and is replaced. Smoothing over
 * that would hide the bug that caused it.
 */

/** Beyond this much disagreement, the prediction is discarded, in meters. */
const RECONCILE_METERS = 6;

/** Snapshots stop arriving for this long and the connection is presumed lost. */
const SILENCE_MS = 4000;



/**
 * A plate this client read off a car, and when.
 *
 * A record with a timestamp, not a track. It is what was true at the moment
 * the plate was legible and it does not update afterward, because a person who
 * glanced at a car does not keep learning about it. That is the whole
 * difference between a witness and a database, and the game is going to make
 * the same distinction with cameras at M2.
 *
 * Kept on the client on purpose: this is memory of something already lawfully
 * seen, no different from writing it on the back of your hand. If it ever
 * matters competitively that a modified client remembers perfectly, the place
 * to move it is the server, not a check here.
 */
export interface Observation {
  id: number;
  plate: string;
  abbr: string;
  /** Meters per second, as it was when read. */
  speed: number;
  /** Index into the county's roadNames, or -1. */
  road: number;
  /** Index into ROAD_CLASSES, or -1 when it was parked. */
  cls: number;
  /** Sim seconds at which it was read. */
  at: number;
  /** The sticker, as it was when read. Memory, not a live lookup. */
  expired: boolean;
}

interface Tracked {
  id: number;
  prevX: number;
  prevY: number;
  prevHeading: number;
  x: number;
  y: number;
  heading: number;
  /** Frame time of the snapshot that produced the current values. */
  at: number;
  seenAt: number;
  /** Livery, which is visible at any distance and so is not gated like a plate. */
  patrol?: boolean;
  lights?: boolean;
}

export interface NetOptions {
  county: County;
  url: string;
  onWelcome?: (
    plate: Plate,
    pin?: string,
    note?: string,
    daySeconds?: number,
    posting?: { agency: string; agencyCameras: number; countyCameras: number },
  ) => void;
  onResult?: (result: QueryResultMessage) => void;
  onAlert?: (alert: AlertMessage) => void;
  onHotlist?: (entries: HotlistRow[]) => void;
  onAudit?: (entries: AuditEntry[], total: number, unexplained: number) => void;
  onCameras?: (seen: SightedCamera[], scope: 'found' | 'coverage') => void;
  onNotice?: (message: string) => void;
  onSharing?: (on: boolean) => void;
  onDispatchBoard?: (board: DispatchBoardMessage) => void;
  onJobBoard?: (board: JobBoardMessage) => void;
  onSources?: (sources: CountySources) => void;
  onSideChanged?: (side: SideChangedMessage) => void;
  onWeek?: (week: WeekMessageReply) => void;
  onStanding?: (report: StandingReplyMessage) => void;
  onCommunityMap?: (markers: CommunityMarker[], yours: number) => void;
  onStopResult?: (result: StopResultMessage) => void;
  /** A pursuit this officer is running started, or ended. */
  onPursuit?: (pursuit: PursuitMessage) => void;
  onError?: (message: string, about?: ErrorMessage['about']) => void;
  /** The keyring changed, so anything showing it should redraw. */
  onKeyring?: () => void;
  /** A remembered key was refused and dropped; this browser is new again. */
  onKeyDropped?: () => void;
  /** This server sells custom plates; the garage should show its box. */
  onPlatesForSale?: () => void;
  /** A pairing code to show, and how long it lasts. */
  onPairingCode?: (code: string, seconds: number) => void;
  /** The administrator's inspector answer for one car. */
  onInspect?: (told: InspectReplyMessage) => void;
  /** The administrator's plate oversight answer: stops and pursuits, and the
   *  journey -- every camera that read the plate, to trace on the map. */
  onOversight?: (plate: string, incidents: IncidentRow[], journey: JourneyPoint[]) => void;
  /** The traffic heatmap: per-camera read counts and reads-by-hour. */
  onHeatmap?: (cells: HeatCell[], hours: number[], total: number) => void;
  /** How many passkeys open this account. */
  onPasskeys?: (saved: number) => void;
  /** Whether a custom plate is available, and its price. */
  onPlateOffer?: (plate: string, ok: boolean, reason: string, priceCents: number) => void;
  /** A Stripe Checkout URL to open for the plate purchase. */
  onPlateCheckout?: (url: string) => void;
  /** This client is wearing a different plate now. */
  onPlate?: (plate: Plate) => void;
  onNeedsPin?: (needs: boolean, plate: string) => void;
  onScanner?: (call: ScannerMessage) => void;
  onAdminState?: (state: AdminStateMessage) => void;
  /** A live oversight event: one read, hit, stop or pursuit. */
  onAdminEvent?: (event: AdminEventMessage) => void;
  /** Ride-along state for the player the administrator is watching. */
  onRideAlong?: (state: RideAlongStateMessage) => void;
  /** A camera the administrator opened: its detail and captures. */
  onCameraDetail?: (state: CameraDetailReplyMessage) => void;
  onResetDone?: (what: 'cameras' | 'world' | 'players', said: string) => void;
  onState?: (state: ConnectionState) => void;
  /** Ask to play the enforcement side. The server decides. */
  enforcement?: boolean;
  /** Claim the reserved plate. The server checks it against its own key. */
  admin?: string;
  /** Do not connect until start() is called. The landing page's option. */
  holdConnection?: boolean;
}

export class NetClient {
  state: ConnectionState = 'connecting';
  plate: Plate | null = null;
  /** Whether the server granted this client the network. */
  enforcement = false;
  /**
   * The pursuit this officer is running, if any: the quarry's id and plate. The
   * position is read off the snapshot each frame, so this only has to name who.
   */
  pursuit: {
    target: number;
    plate: string;
    sight: 'eyes' | 'lost';
    lastPlace?: string | undefined;
    lastHeading?: number | undefined;
  } | null = null;
  /** Whether this patrol car has a radar gun (from the jobs message). */
  radar = false;
  pingMs = 0;

  /** Sim tick and clock, as of the last snapshot. */
  tick = 0;
  time = 0;

  /**
   * A world with no traffic in it, used only to run this client's own vehicle
   * through the shared tick function. Everything else on screen comes off the
   * wire, so nothing else is simulated here.
   */
  private readonly world: World;
  private readonly keyring = new Keyring();
  /** A specific car to come back in, set by switchTo. */
  private wanted: CarKey | null = null;
  /** Whether this player's own lights are running, from the last snapshot. */
  private selfLights = false;
  private lastThrottle = -1;
  private lastWant: number | null = Number.NaN;
  private lastView = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  private self: Vehicle | null = null;
  private selfId = -1;

  private socket: WebSocket | null = null;
  private readonly tracked = new Map<number, Tracked>();
  /** Plates read this instant, replaced wholesale by every snapshot. */
  private readonly readable = new Map<number, PlateRead>();
  /** Plates read at some point, kept until they fade. */
  private readonly remembered = new Map<number, Observation>();
  /** The plate this connection asked for, so a different answer can be noticed. */
  private asked: string | null = null;
  /** Whether that plate was chosen just now, or merely remembered. */
  private askedDeliberately = false;
  /** True when the most recent welcome honored the plate this browser resumed. */
  justResumed = false;
  /** The key typed at a recovery, held until the plates come back. */
  private pendingPin: string | null = null;

  private lastSnapshotAt = 0;
  private snapshotInterval = 1000 / SNAPSHOT_HZ;
  private accumulator = 0;
  private pingSentAt = 0;
  private pingId = 1;
  private reconnectAt = 0;
  private reconnectDelay = 500;
  private reconnectTimer: number | null = null;
  private started = false;

  private readonly options: NetOptions;

  constructor(options: NetOptions) {
    this.options = options;
    this.world = createWorld(options.county, { civilianCount: 0 });
    /**
     * A held connection is the whole point of the landing page.
     *
     * The client used to connect in this constructor, so a brand-new visitor
     * was handed a car before they had read a word or clicked anything -- the
     * landing was a curtain over a game already running, and sign-in from it
     * was "get your cars listed while sitting in a stranger's". Held means no
     * car exists until the player chooses a door.
     */
    if (!options.holdConnection) this.start();
  }

  /** Open the connection. Safe to call twice; the second is a no-op. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.connect();
    this.startReconnectTimer();
  }

  /**
   * End the session and forget this browser's keys.
   *
   * The passkey stays where it lives -- in the device, not here -- so "I have
   * a car here" gets everything back with a touch. What dies is the socket,
   * the reconnect loop, and the resume cache, which together are exactly the
   * things that were skipping the front door.
   */
  signOut(): void {
    this.started = false;
    if (this.reconnectTimer !== null) {
      window.clearInterval(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.keyring.clear();
    this.self = null;
    this.plate = null;
    this.socket?.close();
    this.socket = null;
  }

  get county(): County {
    return this.options.county;
  }

  get selfVehicle(): Vehicle | null {
    return this.self;
  }

  /**
   * The router, so the client can work out what a trip would have been.
   *
   * Same one the simulation uses, so the comparison route the route layer draws
   * is computed exactly the way the server computed the real one.
   */
  get router(): Router {
    return this.world.router;
  }

  // -- socket ---------------------------------------------------------------

  /**
   * Keep trying to be connected, on a clock rather than on frames.
   *
   * This used to live in `update`, which runs off the render ticker -- and a
   * browser throttles or stops animation frames in a background tab, so a
   * client that lost its socket while the player was reading something else
   * stayed disconnected until they came back and painted a frame. Worse, a
   * deliberate car switch closes the socket on purpose, so switching cars in a
   * tab that was not painting hung there permanently.
   *
   * Being connected is not a rendering concern.
   */
  private startReconnectTimer(): void {
    if (this.reconnectTimer !== null) return;
    this.reconnectTimer = window.setInterval(() => {
      if (this.socket) return;
      if (performance.now() < this.reconnectAt) return;
      this.connect();
    }, 250);
  }

  private connect(): void {
    this.setState('connecting');
    const socket = new WebSocket(this.options.url);
    this.socket = socket;

    socket.addEventListener('open', () => {
      /**
       * The car asked for, or the one most recently driven.
       *
       * **Never the remembered one on an admin connection.** The admin URL
       * means "be the administrator's car" -- but auto-resume was sending the
       * keyring's last car anyway, and the server treats any named plate as a
       * deliberate request that outranks the admin claim (so an administrator
       * *can* drive other cars on purpose). Result: registering any car on
       * this browser silently locked the admin URL out of DRT-STAR. A
       * deliberate garage switch (`wanted`) still names a plate and still
       * wins, which is the distinction that matters: chosen beats standing,
       * standing does not beat the URL.
       */
      const deliberate = this.wanted !== null;
      const saved =
        this.wanted ?? (this.options.admin !== undefined ? null : this.keyring.current());
      this.wanted = null;
      this.asked = saved?.plate ?? null;
      this.askedDeliberately = deliberate;
      socket.send(
        encode({
          t: 'hello',
          version: PROTOCOL_VERSION,
          ...(this.options.enforcement ? { enforcement: true } : {}),
          ...(this.options.admin !== undefined ? { admin: this.options.admin } : {}),
          ...(saved ? { plate: saved.plate, pin: saved.pin } : {}),
        }),
      );
      this.reconnectDelay = 500;
    });

    socket.addEventListener('message', (event) => {
      const message = tryDecode<ServerMessage>(event.data);
      if (message) this.receive(message);
    });

    const dropped = (): void => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.setState('lost');
      this.reconnectAt = performance.now() + this.reconnectDelay;
      this.reconnectDelay = Math.min(8000, this.reconnectDelay * 2);
    };
    socket.addEventListener('close', dropped);
    socket.addEventListener('error', dropped);
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.options.onState?.(state);
  }

  private receive(message: ServerMessage): void {
    switch (message.t) {
      case 'welcome': {
        // A resume when the server honored the exact plate this browser asked
        // to come back to. The door reads this to lift for a returning player
        // without a ceremony, while a guest welcome (nothing asked) leaves the
        // gate shut.
        this.justResumed = this.asked !== null && this.asked === message.you.plate.text;
        this.enforcement = message.enforcement === true;
        this.plate = message.you.plate;
        this.selfId = message.you.id;
        // The vehicle the server issued, mirrored locally so the shared tick
        // function has something to advance. Its plate is overwritten with the
        // authoritative one rather than the one the local registry invented.
        const mirror = spawnVehicle(this.world, 'player');
        (mirror as { plate: Plate }).plate = message.you.plate;
        (mirror as { id: number }).id = message.you.id;
        this.self = mirror;
        // Every key this client is handed goes on the ring, so a second car is
        // kept rather than replacing the first.
        //
        // A second tab is no longer a source of extra cars: it is seated in the
        // same one, so there is nothing new to add and `touch` finds the plate
        // already there.
        /**
         * A key to a car that is not on the register any more.
         *
         * The server hands over a different plate when the one asked for does
         * not exist -- after a players reset, or against a fresh database -- and
         * without this the ring keeps the dead key, offers it again on the next
         * connect, and the garage fills with plates that open nothing.
         *
         * A wrong key is a different answer and does not come through here: the
         * server refuses it and closes, so no welcome arrives at all and the
         * key that was merely mistyped is kept.
         */
        if (this.asked && this.asked !== message.you.plate.text) {
          this.keyring.drop(this.asked);
          // A remembered plate the register no longer holds is the same event
          // as a refused key: this browser is new again, and the front door
          // should say so. Without this, the wiped-server case loaded straight
          // into an unexplained replacement car -- no landing, no save ask.
          if (!this.askedDeliberately) this.options.onKeyDropped?.();
        }
        this.asked = null;
        if (message.you.pin) {
          this.keyring.add(message.you.plate.text, message.you.pin);
          // A resume key means a registered car exists: this device has a game
          // worth continuing, and the landing's big button should say so.
          try {
            localStorage.setItem('hotlist.kept', '1');
          } catch {
            // Same as above: cosmetic breadcrumb, safe to lose.
          }
        }
        // The rest of the ring, from the registry. A browser that lost its copy
        // gets the cars back; a browser that still has it learns nothing new.
        if (message.keyring) {
          const key = this.keyring.keyFor(message.you.plate.text);
          if (key) this.keyring.restore(message.keyring, key.pin);
        }
        this.options.onKeyring?.();
        this.options.onNeedsPin?.(message.needsPin === true, message.you.plate.text);
        this.keyring.touch(message.you.plate.text);
        this.options.onWelcome?.(
          message.you.plate,
          message.you.pin,
          message.note,
          message.daySeconds,
          message.agency !== undefined
            ? {
                agency: message.agency,
                agencyCameras: message.agencyCameras ?? 0,
                countyCameras: message.countyCameras ?? 0,
              }
            : undefined,
        );
        if (message.platesForSale) this.options.onPlatesForSale?.();
        this.setState('live');
        return;
      }
      case 'snapshot':
        this.applySnapshot(message);
        return;
      case 'result':
        this.options.onResult?.(message);
        return;
      case 'alert':
        this.options.onAlert?.(message);
        return;
      case 'hotlistState':
        this.options.onHotlist?.(message.entries);
        return;
      case 'auditLog':
        this.options.onAudit?.(message.entries, message.total, message.unexplained);
        return;
      case 'cameras':
        this.options.onCameras?.(message.seen, message.scope ?? 'found');
        return;
      case 'yourWeek':
        this.options.onWeek?.(message);
        return;
      case 'standingReport':
        this.options.onStanding?.(message);
        return;
      case 'sideChanged':
        // The server decides which side somebody is on, so this is the thing
        // that changes the interface -- never the click that asked for it.
        this.enforcement = message.enforcement;
        this.options.onSideChanged?.(message);
        return;
      case 'keyring': {
        /**
         * The plates on this account. Two ways to arrive here.
         *
         * After an ordinary welcome it is the rest of a ring this browser
         * already holds part of, and the key comes off the car being driven.
         * After redeeming a pairing code the browser holds nothing and the key
         * is the one just typed, which is why it was kept.
         */
        const typed = this.pendingPin;
        this.pendingPin = null;
        const driving = this.plate?.text ?? '';
        const pin = typed ?? this.keyring.keyFor(driving)?.pin ?? '';
        if (pin) this.keyring.restore(message.plates, pin);
        this.options.onKeyring?.();
        const first = message.plates[0];
        if (typed && first && first !== driving) this.switchTo(first);
        return;
      }
      case 'inspect':
        this.options.onInspect?.(message);
        return;
      case 'heatmapResult':
        this.options.onHeatmap?.(message.cells, message.hours, message.total);
        return;
      case 'oversightResult':
        this.options.onOversight?.(message.plate, message.incidents, message.journey ?? []);
        return;
      case 'pairingCode':
        this.options.onPairingCode?.(message.code, message.seconds);
        return;
      case 'passkeyOptions':
        void this.answerPasskey(message.how, message.options);
        return;
      case 'passkeyState':
        /**
         * A breadcrumb, not a credential: this browser has saved a game at
         * least once. The landing reads it to decide which door is the big
         * one -- CONTINUE for a device that has something to continue, DRIVE
         * for a stranger -- because the report that forced this was somebody
         * with a saved car clicking the big DRIVE out of habit and getting a
         * second car they never wanted.
         */
        if (message.saved > 0) {
          try {
            localStorage.setItem('hotlist.kept', '1');
          } catch {
            // Storage may be unavailable; the door just stays generic.
          }
        }
        /**
         * Saving the passkey may have registered the car on the spot. The
         * server minted the PIN in that case and sends it once, here, so this
         * browser keeps auto-resuming the ordinary way. The durable identity
         * is the passkey; this is the convenience cache.
         */
        if (message.plate && message.pin) {
          this.keyring.add(message.plate, message.pin);
          this.keyring.touch(message.plate);
        }
        this.options.onPasskeys?.(message.saved);
        return;
      case 'plateOffer':
        this.options.onPlateOffer?.(message.plate, message.ok, message.reason, message.priceCents);
        return;
      case 'plateCheckout':
        this.options.onPlateCheckout?.(message.url);
        return;
      case 'plate': {
        /**
         * The registration went through, and this is the plate it went through
         * under -- which may not be the plate that was issued, because the
         * player is allowed to pick one.
         *
         * This is also where the key goes on the ring. It used to arrive on the
         * welcome, back when the server invented the PIN; now the player picks
         * it and the server has nothing to send back, so nothing was being
         * stored at all. Register, close the tab, and the browser had no idea
         * what car it had just been given.
         */
        const previous = this.plate?.text ?? '';
        this.plate = message.plate;
        if (this.self) (this.self as { plate: Plate }).plate = message.plate;
        /**
         * A bought plate: the account is unchanged, so the passkey still works;
         * only its label is stale. Move the resume key to the new plate keeping
         * the same PIN, and tell the authenticator the account's name is the
         * new plate so the password manager shows it. No new ceremony -- the
         * credential authenticates the account, and the account did not move.
         */
        if (message.userHandle && previous && previous !== message.plate.text) {
          const key = this.keyring.keyFor(previous);
          if (key) {
            this.keyring.add(message.plate.text, key.pin);
            this.keyring.drop(previous);
            this.keyring.touch(message.plate.text);
          }
          try {
            (
              PublicKeyCredential as unknown as {
                signalCurrentUserDetails?: (d: {
                  rpId: string;
                  userId: string;
                  name: string;
                  displayName: string;
                }) => Promise<void>;
              }
            ).signalCurrentUserDetails?.({
              rpId: location.hostname,
              userId: message.userHandle,
              name: message.plate.text,
              displayName: message.plate.text,
            });
          } catch {
            // Relabel is cosmetic; the passkey keeps working regardless.
          }
        }
        const pin = this.pendingPin;
        this.pendingPin = null;
        if (pin) {
          this.keyring.add(message.plate.text, pin);
          this.keyring.touch(message.plate.text);
        }
        this.options.onPlate?.(message.plate);
        this.options.onKeyring?.();
        return;
      }
      case 'sources':
        this.options.onSources?.(message.sources);
        return;
      case 'jobBoard':
        this.radar = message.radar === true;
        this.options.onJobBoard?.(message);
        return;
      case 'dispatchBoard':
        this.options.onDispatchBoard?.(message);
        return;
      case 'communityMap':
        this.options.onCommunityMap?.(message.markers, message.yours);
        return;
      case 'notice':
        // The predicted self has to run at the same speed the server is running
        // it at, or prediction fights the snapshot every tick.
        if (message.speedMultiplier !== undefined && this.self) {
          this.self.speedMultiplier = message.speedMultiplier;
        }
        if (message.sharing !== undefined) this.options.onSharing?.(message.sharing);
        this.options.onNotice?.(message.message);
        return;
      case 'stopResult':
        this.options.onStopResult?.(message);
        return;
      case 'pursuit':
        if (message.active) {
          // Merge, so a mid-chase sight update (eyes<->lost) does not wipe the
          // plate/target the start message set.
          this.pursuit = {
            target: message.target,
            plate: message.plate,
            sight: message.sight ?? this.pursuit?.sight ?? 'eyes',
            lastPlace: message.lastPlace ?? this.pursuit?.lastPlace,
            lastHeading: message.lastHeading ?? this.pursuit?.lastHeading,
          };
        } else {
          this.pursuit = null;
        }
        if (this.self) {
          // Match the cruiser's predicted speed to the allowance the server just
          // granted (or restored), so the car this client draws actually runs at
          // chase speed instead of the posted limit.
          if (message.speedMultiplier !== undefined) {
            this.self.speedMultiplier = message.speedMultiplier;
          }
          // Only the START carries speedMultiplier -- and only the start hands
          // the wheel to the auto-close. A sight update mid-chase must not, or it
          // would yank the wheel back from an officer who took over to search.
          if (message.active && message.speedMultiplier !== undefined) this.self.manual = false;
        }
        this.options.onPursuit?.(message);
        return;
      case 'scanner':
        this.options.onScanner?.(message);
        return;
      case 'adminState':
        this.options.onAdminState?.(message);
        return;
      case 'adminEvent':
        this.options.onAdminEvent?.(message);
        return;
      case 'rideAlongState':
        this.options.onRideAlong?.(message);
        return;
      case 'cameraDetailResult':
        this.options.onCameraDetail?.(message);
        return;
      case 'resetDone':
        this.options.onResetDone?.(message.what, message.said);
        return;
      case 'pong':
        if (message.id === this.pingId) this.pingMs = performance.now() - this.pingSentAt;
        return;
      case 'error':
        /**
         * A dead key, named. The server has never seen this credential, so it
         * tells the browser to bury it: Chrome's Signal API removes the entry
         * from the password manager, and the picker stops offering a key that
         * can only fail. Old browsers simply skip this and keep the corpse.
         */
        if (message.about === 'passkey' && message.credential) {
          try {
            (
              PublicKeyCredential as unknown as {
                signalUnknownCredential?: (told: { rpId: string; credentialId: string }) => void;
              }
            ).signalUnknownCredential?.({
              rpId: location.hostname,
              credentialId: message.credential,
            });
          } catch {
            // Cosmetic cleanup only; the sign-in failure below still handles
            // the moment.
          }
        }
        /**
         * A remembered key the server refused is junk, and keeping it is a
         * trap. The server closes after a wrong-key refusal, the client
         * reconnects, and auto-resume offered the same dead key again --
         * forever, mode "lost", with no way forward short of clearing the
         * browser by hand. A key somebody *typed or clicked* is kept, because
         * a deliberate switch that fails should not eat a key over a typo;
         * a key the browser offered on its own has no such excuse.
         */
        if (message.about === 'key' && this.asked && !this.askedDeliberately) {
          this.keyring.drop(this.asked);
          this.asked = null;
          this.options.onKeyDropped?.();
        }
        this.options.onError?.(message.message, message.about);
        return;
      default:
        return;
    }
  }

  private applySnapshot(snapshot: SnapshotMessage): void {
    const now = performance.now();
    if (this.lastSnapshotAt > 0) {
      // Measured rather than assumed: a server under load or a slow link
      // changes the spacing, and interpolating against the wrong interval is
      // what makes remote traffic stutter.
      const gap = now - this.lastSnapshotAt;
      if (gap > 5 && gap < 500) this.snapshotInterval += (gap - this.snapshotInterval) * 0.15;
    }
    this.lastSnapshotAt = now;
    this.tick = snapshot.tick;
    this.time = snapshot.time;
    this.setState('live');

    this.readable.clear();
    for (const entity of snapshot.entities) {
      if (!entity.read) continue;
      this.readable.set(entity.id, entity.read);
      // Reading it again refreshes it, the way looking twice would.
      this.remembered.set(entity.id, {
        id: entity.id,
        plate: entity.read.p,
        abbr: entity.read.j,
        speed: entity.read.s,
        road: entity.read.r,
        cls: entity.read.c,
        at: snapshot.time,
        expired: entity.read.x === true,
      });
    }
    for (const [id, seen] of this.remembered) {
      if (snapshot.time - seen.at > PLATE_MEMORY_SECONDS) this.remembered.delete(id);
    }

    for (const entity of snapshot.entities) {
      const existing = this.tracked.get(entity.id);
      if (existing) {
        existing.prevX = existing.x;
        existing.prevY = existing.y;
        existing.prevHeading = existing.heading;
        existing.x = entity.x;
        existing.y = entity.y;
        existing.heading = entity.h;
        existing.at = now;
        existing.seenAt = now;
        existing.patrol = entity.patrol === true;
        existing.lights = entity.lights === true;
      } else {
        this.tracked.set(entity.id, {
          id: entity.id,
          prevX: entity.x,
          prevY: entity.y,
          prevHeading: entity.h,
          x: entity.x,
          y: entity.y,
          heading: entity.h,
          at: now,
          seenAt: now,
          patrol: entity.patrol === true,
          lights: entity.lights === true,
        });
      }
    }

    // Anything not in this snapshot has left the interest set. Dropped after a
    // grace period so a single missed frame does not make traffic blink.
    for (const [id, entity] of this.tracked) {
      if (now - entity.seenAt > this.snapshotInterval * 4) this.tracked.delete(id);
    }

    this.reconcile(snapshot);
  }

  /** Take the server's word for it when the prediction has drifted. */
  private reconcile(snapshot: SnapshotMessage): void {
    const self = this.self;
    if (!self) return;

    if (snapshot.route) {
      self.route = snapshot.route;
      self.routeIndex = snapshot.you.routeIndex;
      this.selfLights = snapshot.you.lights === true;
    }

    const authoritative = {
      edge: snapshot.you.edge,
      forward: snapshot.you.forward,
      offset: snapshot.you.offset,
      atNode: snapshot.you.atNode,
    };
    self.speed = snapshot.you.speed;
    self.destination = snapshot.you.destination;
    // The tank is the server's to know; the client only shows it.
    self.fuel = snapshot.you.fuel;

    // Compare the whole graph position, not just how far along an edge.
    //
    // The obvious version of this compared edge and offset only, and a parked
    // vehicle has edge -1 and offset 0 no matter *where* it is parked. So two
    // clients whose local mirrors happened to spawn at different nodes both
    // read as "in agreement with the server" and neither was ever corrected,
    // which drew a stationary car at a junction it was not at. Anything the
    // server considers part of a position has to be part of this test.
    const agreed =
      self.edge === authoritative.edge &&
      (authoritative.edge < 0
        ? self.atNode === authoritative.atNode
        : self.forward === authoritative.forward);
    const drift = agreed ? Math.abs(self.offset - authoritative.offset) : Infinity;

    if (drift > RECONCILE_METERS) {
      self.edge = authoritative.edge;
      self.forward = authoritative.forward;
      self.offset = authoritative.offset;
      self.atNode = authoritative.atNode;
      // Rebased by the server against the route this client holds, so it can
      // be taken literally.
      self.routeIndex = snapshot.you.routeIndex;
      this.selfLights = snapshot.you.lights === true;
      resnap(this.world, self);
    }
  }

  // -- per frame ------------------------------------------------------------

  /**
   * Advance prediction and reconnect if the socket went away. `elapsed` is in
   * seconds of real time.
   */
  update(elapsed: number): void {
    const now = performance.now();

    // Reconnection is on a timer rather than here; see startReconnectTimer.
    // A dropped connection has to recover whether or not frames are painting.
    if (this.state === 'live' && now - this.lastSnapshotAt > SILENCE_MS && this.lastSnapshotAt > 0) {
      this.setState('lost');
    }

    if (this.socket?.readyState === WebSocket.OPEN && now - this.pingSentAt > 2000) {
      this.pingSentAt = now;
      this.pingId++;
      this.socket.send(encode({ t: 'ping', id: this.pingId }));
    }

    // Prediction runs on the same fixed step as the server, because a variable
    // step would diverge from it by construction.
    if (this.self) {
      this.accumulator += Math.min(0.25, elapsed);
      let steps = 0;
      while (this.accumulator >= TICK_SECONDS && steps < 30) {
        stepVehicle(this.world, this.self);
        this.accumulator -= TICK_SECONDS;
        steps++;
      }
    }
  }

  /** Everything this client may draw, interpolated for right now. */
  renderables(): Renderable[] {
    const now = performance.now();
    const items: Renderable[] = [];

    for (const entity of this.tracked.values()) {
      const alpha = Math.min(1, (now - entity.at) / this.snapshotInterval);
      items.push({
        id: entity.id,
        x: entity.prevX + (entity.x - entity.prevX) * alpha,
        y: entity.prevY + (entity.y - entity.prevY) * alpha,
        heading: lerpAngle(entity.prevHeading, entity.heading, alpha),
        kind: entity.patrol ? 'patrol' : 'civilian',
        ...(entity.lights ? { lights: true } : {}),
      });
    }

    const self = this.self;
    if (self) {
      const alpha = Math.min(1, this.accumulator / TICK_SECONDS);
      items.push({
        id: self.id,
        x: self.prevX + (self.x - self.prevX) * alpha,
        y: self.prevY + (self.y - self.prevY) * alpha,
        heading: self.heading,
        // On shift you are driving a marked unit, and it should look like one
        // to you as well as to everybody else.
        kind: this.enforcement ? 'patrol' : 'player',
        self: true,
        // Running-code lights are an enforcement thing only. A civilian who was
        // just on duty can carry a stale you.lights=true out of the switch, and
        // rendering it put cop lights on a civilian car. Gate it to the side.
        ...(this.selfLights && this.enforcement ? { lights: true } : {}),
      });
    }

    return items;
  }

  // -- intents --------------------------------------------------------------

  /**
   * Search the detection table.
   *
   * Refused unless the server said this client may, which it says on the
   * welcome. A client cannot grant itself the network.
   */
  search(by: 'plate' | 'camera', term: string, reason = ''): void {
    if (!this.enforcement) return;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'query', by, term, ...(reason ? { reason } : {}) }));
  }

  /**
   * Tell the server which roads to route around.
   *
   * Only ever edges this client discovered by driving them, so it cannot buy
   * knowledge the player has not earned. The server penalizes them rather than
   * banning them; see AVOID_EDGE_PENALTY.
   */
  avoid(edges: number[]): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'avoid', edges }));
  }

  /**
   * File what you have found and ask for what everybody else has.
   *
   * The server only accepts ids this session was actually near, so a client
   * cannot map the county without driving it.
   */
  report(share: string[]): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'report', share }));
  }

  /** Ask what your agency must send somebody out for, or say you looked. */
  dispatch(camera?: string): void {
    if (!this.enforcement) return;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'dispatch', ...(camera ? { camera } : {}) }));
  }

  /** Ask what the record holds about your own plate. */
  week(): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'week' }));
  }

  /** Ask for your standing: rank, department tier, and operational budget. */
  standing(): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'standing' }));
  }

  /** Pick a side, or change sides. The server decides. */
  side(want: 'civilian' | 'enforcement'): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'side', want }));
  }

  /** Ask what work is going, take a job, or hand one in. */
  job(action?: 'take' | 'arrive' | 'abandon', id?: string): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'job', ...(action ? { action } : {}), ...(id ? { job: id } : {}) }));
  }

  /** Spend: renew the tag, or buy a second car. */
  buy(what: 'registration' | 'vehicle' | 'fuel' | 'scanner' | 'radar' | 'garage'): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'buy', what }));
  }

  /**
   * Drive a different car you already hold the key to.
   *
   * Reconnects rather than switching in place, because seating somebody in
   * another vehicle is exactly what a fresh hello does and doing it twice in
   * two ways would be two things to keep correct.
   */
  switchTo(plate: string): void {
    const key = this.keyring.keyFor(plate);
    if (!key) return;
    this.wanted = key;
    // A deliberate switch is not a dropped connection, so it must not inherit
    // the failure backoff -- which after a few earlier drops is eight seconds,
    // long enough that the player concludes the button is broken.
    this.reconnectDelay = 0;
    this.reconnectAt = 0;
    const socket = this.socket;
    this.socket = null;
    socket?.close();
  }

  /** The cars this client holds keys to. */
  get cars(): readonly CarKey[] {
    return this.keyring.all();
  }

  /**
   * Take a key somebody typed in.
   *
   * The server decides whether it opens anything: this only writes it down so
   * the next hello carries it. A wrong key is refused there and nothing is
   * issued, which is the whole of the check.
   */
  remember(plate: string, pin: string): void {
    this.keyring.add(plate, pin);
  }

  /**
   * Ask what the administration panel shows, or reset something.
   *
   * The key goes with every one of these rather than being trusted from the
   * session: being on shift makes somebody an officer, and an administrator can
   * empty the county. The server checks it again either way.
   */
  admin(): void {
    const key = this.options.admin;
    if (key === undefined) return;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'admin', admin: key }));
  }

  reset(what: 'cameras' | 'world' | 'players'): void {
    const key = this.options.admin;
    if (key === undefined) return;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'reset', what, admin: key }));
  }

  /**
   * Turn the shared network on or off.
   *
   * One boolean between a department's own handful of cameras and everybody's.
   */
  share(on: boolean): void {
    if (!this.enforcement) return;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'share', on }));
  }

  /** Run at up to twice the limit. Administrators only; the server checks. */
  run(fast: boolean): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'run', fast }));
  }

  /**
   * Interfere with a camera. The server checks you are actually there, and how
   * close it wants you depends on which tier you asked for.
   */
  obstruct(camera: string, how: Interference = 'obstruct', means?: Obstruction): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'obstruct', camera, how, ...(means ? { with: means } : {}) }));
  }

  /**
   * Drive by hand.
   *
   * Sent on change, not per frame: three states per axis and keys held for
   * seconds means a handful of messages per junction rather than sixty a
   * second. The last state is remembered so a repeat is dropped.
   */
  steer(throttle: number, want: number | null): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    if (throttle === this.lastThrottle && want === this.lastWant) return;
    this.lastThrottle = throttle;
    this.lastWant = want;
    // The prediction runs the same tick function the server does, so it has to
    // be told the same thing. Without this the client keeps driving at the
    // limit while the server slows to a stop, and every snapshot yanks the car
    // backwards -- prediction fighting the authority rather than following it.
    if (this.self) {
      this.self.manual = true;
      this.self.throttle = throttle;
      this.self.want = want ?? undefined;
    }
    this.socket.send(encode({ t: 'steer', throttle, want }));
  }

  /**
   * Say what is on screen, so the server can send the traffic that is in it.
   *
   * Sent when the view has moved or zoomed enough to matter rather than every
   * frame: this is a hint for culling, not a control input, and a message a
   * frame for a rectangle that barely changed is the sort of thing that shows
   * up as a busy socket and nothing else.
   */
  view(minX: number, minY: number, maxX: number, maxY: number): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    const wide = maxX - minX;
    const moved =
      Math.abs(minX - this.lastView.minX) > wide * 0.15 ||
      Math.abs(minY - this.lastView.minY) > wide * 0.15 ||
      Math.abs(wide - (this.lastView.maxX - this.lastView.minX)) > wide * 0.15;
    if (!moved) return;
    this.lastView = { minX, minY, maxX, maxY };
    this.socket.send(encode({ t: 'view', minX, minY, maxX, maxY }));
  }

  /** Register this car with a key the player chose. */
  /**
   * Save a passkey, or sign in with one.
   *
   * Two round trips: the server hands over WebAuthn's own options, the browser
   * talks to the authenticator, and whatever comes back goes straight to the
   * server to be checked. Nothing in between is this game's business -- and
   * nothing at all is typed.
   */
  passkey(how: 'save' | 'open'): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    if (!('credentials' in navigator) || !window.PublicKeyCredential) {
      this.options.onError?.('this browser has no passkeys', 'passkey');
      return;
    }
    this.socket.send(encode({ t: 'passkeyStart', how }));
  }

  /**
   * The authenticator's answer, handed back for checking.
   *
   * A refusal at the sensor -- cancelling the dialog, no key on the device --
   * throws here rather than returning, and it is not an error worth shouting
   * about: somebody changed their mind.
   */
  private async answerPasskey(how: 'save' | 'open', options: unknown): Promise<void> {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    try {
      const response =
        how === 'save'
          ? await startRegistration({ optionsJSON: options as never })
          : await startAuthentication({ optionsJSON: options as never });
      this.socket.send(encode({ t: 'passkeyFinish', how, response }));
    } catch {
      /**
       * Cancelled or failed, either ceremony, surfaced either way.
       *
       * The gate needs to hear a cancelled *create* so it can stay shut and
       * say why -- a save is no longer an optional nicety inside the game, it
       * is the only way through the front door, so silence here would strand
       * the player behind an overlay with no explanation.
       */
      let message = 'nothing was signed';
      try {
        // On Windows a built-in (platform) passkey is Windows Hello, and
        // Windows Hello needs a PIN. With no PIN set, the browser hides the
        // "this device" option and the create ceremony dies with a useless
        // "your device can't be used with this site", leaving only USB/phone.
        // Detect the missing platform authenticator on a save and say the
        // actual fix instead of a generic failure -- nobody should have to
        // guess it.
        if (
          how === 'save' &&
          typeof PublicKeyCredential !== 'undefined' &&
          typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function' &&
          !(await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())
        ) {
          message =
            'No passkey is set up on this device. On Windows, add a PIN under ' +
            'Settings › Accounts › Sign-in options, then try again — ' +
            'or use your phone or a security key.';
        }
      } catch {
        // The availability probe is best-effort; fall back to the generic line.
      }
      this.options.onError?.(message, 'passkey');
    }
  }

  /** The administrator asking what a selected car is up to. */
  inspect(id: number): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'inspect', id }));
  }

  /** The administrator asking a plate's enforcement record: its stops and pursuits. */
  oversight(plate: string): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'oversight', plate }));
  }

  /** The administrator asking for the traffic heatmap. */
  heatmap(): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'heatmap' }));
  }

  /** The administrator asking to ride along with a connected player. */
  rideAlong(id: number): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'rideAlong', id }));
  }

  /** The administrator opening a camera to visit it and see its captures. */
  cameraDetail(id: string): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'cameraDetail', id }));
  }

  /** Ask whether a custom plate is free and legal. */
  plateCheck(plate: string, state: string): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'plateCheck', plate, state }));
  }

  /** Buy the custom plate; the reply is a Checkout URL to open. */
  plateBuy(plate: string, state: string): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'plateBuy', plate, state }));
  }

  /** Ask for a code that puts this keyring on another device. */
  askPairing(): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'pairing' }));
  }

  /**
   * Redeem a pairing code.
   *
   * **A plate is not accepted here, and that is the point.** Plates in this
   * game are read by every camera in the county on purpose -- they are the one
   * string that is guaranteed public -- so plate-plus-PIN was a username
   * broadcast to the world with four digits behind it. Getting in from nowhere
   * goes through a code from a device you already hold, or through the spare
   * key, which is a place and cannot be observed at all.
   */
  useKey(code: string, pin: string): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.pendingPin = pin;
    this.socket.send(encode({ t: 'pair', code, pin }));
  }

  setPin(pin: string, plate?: string): void {
    // Held until the server confirms, because the plate that comes back may not
    // be the one this car is wearing now -- the player may have just chosen a
    // different one, and the key belongs to whatever it ends up being.
    this.pendingPin = pin;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'setPin', pin, ...(plate ? { plate } : {}) }));
    // Remembered locally too, so the map does not have to ask again this
    // session. The database is the record; this is the convenience.
    if (this.plate) this.keyring.add(this.plate.text, pin);
  }

  /** Ask for this unit's own search history. */
  audit(limit?: number): void {
    if (!this.enforcement) return;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'audit', ...(limit ? { limit } : {}) }));
  }

  /** Put a plate on the hotlist, take one off, or ask what is on it. */
  hotlist(action: 'add' | 'clear' | 'list', plate?: string, reason?: string, note?: string): void {
    if (!this.enforcement) return;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(
      encode({
        t: 'hotlist',
        action,
        ...(plate ? { plate } : {}),
        ...(reason ? { reason } : {}),
        ...(note ? { note } : {}),
      }),
    );
  }

  /** Pull a vehicle over. The server decides whether you were close enough. */
  pullOver(id: number): void {
    if (!this.enforcement) return;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'pullover', id }));
  }

  /** Light a vehicle up and give chase. The server checks line of sight. */
  pursue(id: number): void {
    if (!this.enforcement) return;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'pursue', id }));
  }

  /** Break off the pursuit this officer is running. */
  breakoff(): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'breakoff' }));
  }

  /** Send a unit after a plate. The server checks the officer has intel on it. */
  dispatchUnit(plate: string): void {
    if (!this.enforcement) return;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'dispatchUnit', plate }));
  }

  drive(node: number): void {
    // Handing the wheel back, on the prediction as well as on the server.
    if (this.self) {
      this.self.manual = false;
      this.self.throttle = 1;
      this.self.want = undefined;
    }
    this.lastThrottle = -1;
    this.lastWant = Number.NaN;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'drive', node }));
  }

  stop(): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'stop' }));
  }

  /** Take whatever is parked nearest. The server picks it and says no if it will not. */
  steal(): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encode({ t: 'steal' }));
  }

  get visibleCount(): number {
    return this.tracked.size + (this.self ? 1 : 0);
  }

  /** How many plates are legible right now. */
  get readableCount(): number {
    return this.readable.size;
  }

  /** How many plates this player is still carrying in their head. */
  get rememberedCount(): number {
    return this.remembered.size;
  }

  /** The plate on that vehicle right now, if it is close enough to read. */
  reading(id: number): PlateRead | undefined {
    return this.readable.get(id);
  }

  /** What this player saw on that vehicle, whenever that was. */
  recall(id: number): Observation | undefined {
    return this.remembered.get(id);
  }

  get selfVehicleId(): number {
    return this.selfId;
  }
}

/**
 * Recompute the mirrored vehicle's world position after its graph position was
 * replaced. The sim normally does this at the end of a step; a reconcile jumps
 * the vehicle without stepping it.
 */
function resnap(world: World, vehicle: Vehicle): void {
  stepVehicle(world, vehicle, 0);
  vehicle.prevX = vehicle.x;
  vehicle.prevY = vehicle.y;
  vehicle.prevHeading = vehicle.heading;
}

function lerpAngle(from: number, to: number, alpha: number): number {
  let delta = to - from;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return from + delta * alpha;
}

/**
 * The plate and its key, in local storage.
 *
 * DESIGN.md section 4: written automatically so it usually never has to be
 * typed, and shown once so it can be written down. It unlocks a fictional car
 * and nothing else, and the UI says so.
 */


